package core.validation.field

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import core.validation.EsInputState
import core.validation.validator.Validator

/**
 * Represents a field that can be validated.
 *
 * @param initialValue The initial value of the field.
 * @param validators The list of validators to apply to the field.
 */
open class FieldImpl<T : Any>(
    initialValue: T,
    private vararg val validators: Validator<T>,
) : Field {
    /**
     * The error message of the field.
     */
    var errorMessage by mutableStateOf<String?>(null)
        private set

    /**
     * The state of the field.
     */
    final override var state by mutableStateOf(EsInputState.Default)
        private set

    /**
     * The value of the field.
     */
    var value: T = initialValue

    /**
     * Validates the field using the current value.
     */
    override fun validate(): Boolean = validate(value)

    /**
     * Validates the field.
     *
     * The field is considered valid if all the validators pass.
     *
     * The state of the field is updated to [EsInputState.Success] if the value is valid, and [EsInputState.Error] otherwise.
     *
     * The error message is set to the error message of the first validator that fails, or `null` if the value is valid.
     *
     * @param value The value to validate.
     * @return `true` if the value is valid, `false` otherwise.
     */
    fun validate(value: T): Boolean {
        this.value = value

        val failedValidator = validators.firstOrNull { !it.validate(value) }

        state =
            if (failedValidator == null) {
                EsInputState.Default
            } else {
                EsInputState.Error
            }

        errorMessage = failedValidator?.errorMessage

        return failedValidator == null
    }

    /**
     * Resets the field.
     */
    override fun reset() {
        state = EsInputState.Default
        errorMessage = null
    }
}
